<template>
  <div>
    <!-- Cabeçalho: seletor de versão + toggle de modo -->
    <b-card
      class="mb-2"
      body-class="p-2"
    >
      <div class="d-flex align-items-center flex-wrap">
        <div class="mr-3">
          <div class="small text-muted">
            Bot
          </div>
          <div>
            <b-badge variant="primary">
              #{{ botId || 'novo' }}
            </b-badge>
            <b-badge
              v-if="activeVersionNumber"
              variant="success"
              class="ml-1"
            >
              ativa: v{{ activeVersionNumber }}
            </b-badge>
          </div>
        </div>

        <div
          class="ml-auto d-flex align-items-center"
          style="min-width: 360px;"
        >
          <div class="flex-grow-1">
            <b-input-group size="sm">
              <b-input-group-prepend is-text>
                Versão
              </b-input-group-prepend>
              <b-form-select
                v-model="selectedVersion"
                :options="versionOptions"
                @change="onChangeVersion($event)"
              />
              <b-input-group-append>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  @click="reloadVersions"
                >
                  Atualizar
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <small class="text-muted">Ao abrir, carregamos a versão <b>ativa</b>. Você pode trocar aqui.</small>
          </div>

          <!-- Toggle de modo -->
          <div class="ml-2 mt-2 mt-md-0">
            <b-button-group size="sm">
              <b-button
                :variant="editorMode==='form' ? 'primary' : 'outline-primary'"
                @click="editorMode='form'"
              >
                Lista
              </b-button>
              <b-button
                :variant="editorMode==='diagram' ? 'primary' : 'outline-primary'"
                @click="editorMode='diagram'"
              >
                Diagrama
              </b-button>
            </b-button-group>
          </div>
        </div>
      </div>
    </b-card>

    <!-- ============ MODO LISTA (editor atual) ============ -->
    <b-row v-if="!isDiagram">
      <!-- Coluna: lista / configs -->
      <b-col
        cols="12"
        md="4"
      >

        <!-- Configurações do fluxo -->
        <b-card
          class="mb-2"
          body-class="p-2"
        >
          <div class="font-weight-600 mb-2">
            Configurações do fluxo
          </div>

          <b-form-group label="Auto-close (minutos)">
            <b-input-group size="sm">
              <b-form-input
                v-model.number="flow.settings.autoclose_minutes"
                type="number"
                min="0"
                placeholder="Ex.: 15"
                @input="onFlowChange"
              />
              <b-input-group-append is-text>
                min
              </b-input-group-append>
            </b-input-group>
            <small class="text-muted d-block">
              0 desabilita o auto-close. O cronômetro reinicia quando o bot envia/recebe mensagens.
            </small>
          </b-form-group>
        </b-card>

        <b-card class="mb-2">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="font-weight-600">
              Estados
            </div>
            <div>
              <b-button
                size="sm"
                variant="outline-primary"
                @click="addState"
              >
                + estado
              </b-button>
            </div>
          </div>

          <div class="flow-states-list">
            <b-list-group>
              <b-list-group-item
                v-for="name in stateNames"
                :key="name"
                :active="selectedState===name"
                class="d-flex justify-content-between align-items-center"
                @click="selectedState=name"
              >
                <div class="mr-2">
                  <div class="font-weight-600">
                    {{ name }}
                  </div>
                  <div class="text-muted small">
                    {{ stateSummary(name) }}
                  </div>
                </div>

                <div class="text-nowrap">
                  <b-button
                    v-b-tooltip.hover
                    size="sm"
                    variant="link"
                    class="p-0 mr-1"
                    title="Renomear"
                    @click.stop="renameState(name)"
                  >
                    <i class="feather icon-edit" />
                  </b-button>
                  <b-button
                    v-b-tooltip.hover
                    size="sm"
                    variant="link"
                    class="p-0 text-danger"
                    title="Remover"
                    @click.stop="removeState(name)"
                  >
                    <i class="feather icon-trash-2" />
                  </b-button>
                </div>
              </b-list-group-item>
            </b-list-group>
          </div>

          <b-form-group
            label="Estado inicial (start)"
            class="mt-2"
          >
            <b-form-select
              v-model="flow.start"
              :options="stateOptions"
              @change="onFlowChange"
            />
          </b-form-group>
        </b-card>

        <!-- Ações de versão -->
        <b-card>
          <div class="font-weight-600 mb-2">
            Versão
          </div>
          <div class="mb-2">
            <b-button
              size="sm"
              variant="primary"
              class="mr-1"
              :disabled="!isValid"
              @click="saveDraft"
            >
              Salvar rascunho
            </b-button>
            <b-button
              size="sm"
              variant="warning"
              class="mr-1"
              :disabled="!lastDraftVersion"
              @click="publishLastDraft"
            >
              Publicar rascunho
            </b-button>
            <b-button
              size="sm"
              variant="outline-primary"
              :disabled="!botId"
              @click="activateOnBot"
            >
              Ativar no bot
            </b-button>
          </div>
          <div class="small text-muted">
            <div>Último rascunho: <b>{{ lastDraftVersion ? ('v'+lastDraftVersion) : '-' }}</b></div>
            <div>Ativa: <b>{{ activeVersionNumber ? ('v'+activeVersionNumber) : '-' }}</b></div>
          </div>

          <hr>
          <div class="font-weight-600 mb-1">
            Validação
          </div>
          <b-badge :variant="isValid ? 'success' : 'danger'">
            {{ isValid ? 'OK' : 'Inválido' }}
          </b-badge>
          <ul
            v-if="!isValid && validationErrors.length"
            class="small mt-2 mb-0 pl-3"
          >
            <li
              v-for="(e, i) in validationErrors"
              :key="i"
            >
              {{ humanAjvError(e) }}
            </li>
          </ul>

          <hr>
          <b-button
            size="sm"
            variant="outline-secondary"
            @click="openJsonPreview"
          >
            Pré-visualizar JSON
          </b-button>
        </b-card>
      </b-col>

      <!-- Coluna: editor do estado selecionado -->
      <b-col
        cols="12"
        md="8"
      >
        <b-card
          v-if="selectedState"
          class="mb-2"
          body-class="p-2"
        >
          <div class="d-flex justify-content-between align-items-center mb-1">
            <div class="font-weight-600">
              Editar estado: {{ selectedState }}
            </div>
          </div>

          <!-- ON ENTER -->
          <b-card
            class="mb-2"
            body-class="p-2"
          >
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="small text-muted">Ao entrar (on_enter)</span>
              <b-button
                size="sm"
                variant="outline-primary"
                @click="addAction(selectedState, 'on_enter')"
              >
                + ação
              </b-button>
            </div>
            <action-list
              :actions.sync="flow.states[selectedState].on_enter"
              :state-names="stateNames"
              :state-name="selectedState"
              :state-rules="flow.states[selectedState].on_message"
              :bot-uuid="botUuid"
              :auto-rules="true"
              @create-state="createStateFromChild"
              @ensure-state="ensureStateFromChild"
              @rename-state="renameStateFromChild"
              @rule-for="onAutoRuleFor"
              @rule-rename="onAutoRuleRename"
              @rule-remove="onAutoRuleRemove"
              @change="onFlowChange"
            />
          </b-card>

          <!-- ON MESSAGE -->
          <b-card
            class="mb-2"
            body-class="p-2"
          >
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="small text-muted">Regras (on_message)</span>
              <b-button
                size="sm"
                variant="outline-primary"
                @click="addRule(selectedState)"
              >
                + regra
              </b-button>
            </div>
            <rule-list
              :rules.sync="flow.states[selectedState].on_message"
              :state-names="stateNames"
              :auto-interactive-ids="autoInteractiveIdsFor(selectedState)"
              :bot-uuid="botUuid"
              @change="onFlowChange"
              @create-state="createStateFromChild"
            />
          </b-card>
        </b-card>

        <b-alert
          v-else
          show
          variant="light"
        >
          Selecione um estado para editar.
        </b-alert>
      </b-col>
    </b-row>

    <!-- ============ MODO DIAGRAMA (flowBuilder.vue) ============ -->
    <div v-else>
      <b-card
        body-class="p-0"
        class="mb-2"
      >
        <FlowBuilder
          :value="flow"
          @input="onDiagramChange"
        />
      </b-card>

      <b-card>
        <div class="d-flex align-items-center flex-wrap">
          <b-badge :variant="isValid ? 'success' : 'danger'">
            {{ isValid ? 'Fluxo válido' : 'Fluxo inválido' }}
          </b-badge>
          <b-button
            size="sm"
            class="ml-2"
            variant="outline-secondary"
            @click="openJsonPreview"
          >
            Pré-visualizar JSON
          </b-button>
          <b-button
            size="sm"
            class="ml-2"
            variant="primary"
            :disabled="!isValid"
            @click="saveDraft"
          >
            Salvar rascunho
          </b-button>
          <b-button
            size="sm"
            class="ml-2"
            variant="warning"
            :disabled="!lastDraftVersion"
            @click="publishLastDraft"
          >
            Publicar rascunho
          </b-button>
          <b-button
            size="sm"
            class="ml-2"
            variant="outline-primary"
            :disabled="!botId"
            @click="activateOnBot"
          >
            Ativar no bot
          </b-button>
        </div>
        <div class="small text-muted mt-2">
          Último rascunho: <b>{{ lastDraftVersion ? ('v'+lastDraftVersion) : '-' }}</b> •
          Ativa: <b>{{ activeVersionNumber ? ('v'+activeVersionNumber) : '-' }}</b>
        </div>
        <ul
          v-if="!isValid && validationErrors.length"
          class="small mt-2 mb-0 pl-3"
        >
          <li
            v-for="(e, i) in validationErrors"
            :key="i"
          >
            {{ humanAjvError(e) }}
          </li>
        </ul>
      </b-card>
    </div>

    <!-- Modal: JSON -->
    <b-modal
      id="modal-json"
      title="JSON do fluxo"
      ok-only
      size="lg"
    >
      <pre
        class="mb-0"
        style="white-space: pre-wrap"
      >{{ prettyFlow }}</pre>
    </b-modal>
  </div>
</template>

<script>
import Ajv from 'ajv'
import ActionList from './ActionList.vue'
import FlowBuilder from './FlowBuilder.vue' // <<< NOVO
import { FlowsAPI } from '../../../functions/bot/api'
import botFlowSchema from '../../../functions/bot/validation/botFlowSchema'

/** ===== RuleList (local) — com badge de “gerada automaticamente” ===== */
const RuleList = {
  name: 'rule-list',
  components: { ActionList },
  props: {
    rules: { type: Array, default: () => [] },
    stateNames: { type: Array, default: () => [] },
    autoInteractiveIds: { type: Array, default: () => [] },
    botUuid: { type: String, default: null },
  },
  data: () => ({ local: [] }),
  created() { this.syncFromProps() },
  watch: { rules: { handler() { this.syncFromProps() }, deep: true } },
  methods: {
    syncFromProps() { this.local = Array.isArray(this.rules) ? JSON.parse(JSON.stringify(this.rules)) : [] },
    addRule() { this.local.push({ if: {}, then: [] }); this.emitChange() },
    removeRule(i) { this.local.splice(i, 1); this.emitChange() },
    addAction(i) {
      const r = this.local[i]
      r.then = r.then || []
      r.then.push({ action: 'send_text', text: '' })
      this.emitChange()
    },
    emitChange() { this.$emit('update:rules', this.local); this.$emit('change') },
    isAutoRule(r) {
      const id = r && r.if && r.if.interactive_reply_id
      if (!id) return false
      return this.autoInteractiveIds.indexOf(id) !== -1
    },
  },
  template: `
    <div>
      <div v-if="!local.length" class="text-muted small">Nenhuma regra ainda.</div>
      <b-card v-for="(r, i) in local" :key="i" class="mb-2" body-class="p-2">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <div class="small text-muted">Regra #{{ i+1 }}</div>
          <div>
            <b-badge v-if="isAutoRule(r)" variant="info">gerada automaticamente</b-badge>
            <b-button size="sm" variant="outline-danger" class="ml-2" @click="removeRule(i)">remover</b-button>
          </div>
        </div>

        <div class="d-flex align-items-start">
          <div class="flex-grow-1 mr-2">
            <div class="small text-muted mb-1">Condições</div>
            <div class="mb-2 d-flex align-items-center">
              <b-form-input v-model="r.if.interactive_reply_id" placeholder="interactive_reply_id (opcional)" class="mr-2" @input="emitChange"/>
              <span class="text-muted small">ou</span>
            </div>
            <div>
              <b-form-tags
                v-model="r.if.text_includes"
                separator="," remove-on-delete no-outer-focus
                placeholder="text_includes: digite e tecle Enter"
                @input="emitChange"
              />
            </div>
          </div>
        </div>

        <div class="mt-2">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <span class="small text-muted">Ações (then)</span>
            <b-button size="sm" variant="outline-primary" @click="addAction(i)">+ ação</b-button>
          </div>
          <action-list
            :actions.sync="r.then"
            :state-names="stateNames"
            :bot-uuid="botUuid"
            @change="emitChange"
          />
        </div>
      </b-card>

      <b-button size="sm" variant="outline-secondary" @click="addRule">+ regra</b-button>
    </div>
  `,
}

export default {
  name: 'BotFlowEditor',
  components: { ActionList, RuleList, FlowBuilder },
  props: {
    storeId: { type: [String, Number], default: null },
    botId: { type: [String, Number], default: null },
    botUuid: { type: String, default: null },
  },
  data() {
    return {
      ajv: new Ajv({ allErrors: true, strict: false }),
      validate: null,
      validationErrors: [],
      botName: '',
      activeVersion: null,
      lastDraftVersion: null,
      flow: this.defaultFlow(),
      selectedState: null,

      // versões
      versions: [],
      selectedVersion: null,
      activeVersionNumber: null,

      // modo do editor
      editorMode: 'form', // 'form' | 'diagram'
    }
  },
  computed: {
    stateNames() { return Object.keys(this.flow.states || {}) },
    stateOptions() { return this.stateNames.map(s => ({ value: s, text: s })) },

    /** Validação agora usa o fluxo SANITIZADO */
    validatableFlow() { return this.sanitizeFlowForSchema(this.flow) },
    isValid() {
      if (!this.validate) return false
      const ok = this.validate(this.validatableFlow)
      this.validationErrors = ok ? [] : (this.validate.errors || [])
      return ok
    },
    /** Pré-visualização também sanitizada (o que será salvo) */
    prettyFlow() { return JSON.stringify(this.validatableFlow, null, 2) },

    versionOptions() {
      const arr = this.versions.slice().sort((a, b) => b.version - a.version)
      const act = this.activeVersionNumber
      return arr.map(v => {
        const label = `v${v.version}${act && v.version === act ? ' (ativo)' : ''}`
        return { value: String(v.version), text: label }
      })
    },

    // modo atual
    isDiagram() { return this.editorMode === 'diagram' },
  },
  created() {
    this.validate = this.ajv.compile(botFlowSchema)
    if (this.botId) {
      this.loadActive()
      this.reloadVersions()
    } else {
      this.botName = 'Novo Bot'
    }
  },
  methods: {
    defaultFlow() {
      return {
        settings: { autoclose_minutes: 0 },
        start: 'menu',
        states: { menu: { on_enter: [], on_message: [] } },
      }
    },
    humanAjvError(err) {
      const path = (err.instancePath || err.dataPath || '').replace(/^\//, '') || '(raiz)'
      return `${path}: ${err.message}`
    },

    /* ================= SANITIZAÇÃO (remove campos de UI) ================= */
    sanitizeFlowForSchema(src) {
      let out = JSON.parse(JSON.stringify(src || {}))
      if (!out || typeof out !== 'object') out = this.defaultFlow()

      // settings
      const ac = out.settings?.autoclose_minutes
      const n = (typeof ac === 'string') ? parseInt(ac, 10) : ac
      if (Number.isInteger(n) && n >= 0) {
        out.settings = { autoclose_minutes: n }
      } else {
        delete out.settings
      }

      out.states = out.states || {}
      Object.keys(out.states).forEach(stateName => {
        const st = out.states[stateName] || {}
        if (!Array.isArray(st.on_enter)) st.on_enter = []
        if (!Array.isArray(st.on_message)) st.on_message = []

        // on_enter
        st.on_enter = st.on_enter.map(a => this.sanitizeAction(a))

        // on_message
        st.on_message = st.on_message.map(r => {
          const rr = { if: {}, then: [] }
          if (r && r.if) {
            if (typeof r.if.interactive_reply_id === 'string' || typeof r.if.interactive_reply_id === 'number') {
              rr.if.interactive_reply_id = r.if.interactive_reply_id
            }
            if (Array.isArray(r.if.text_includes)) rr.if.text_includes = r.if.text_includes.slice()
          }
          rr.then = Array.isArray(r.then) ? r.then.map(a => this.sanitizeAction(a)) : []
          return rr
        })

        out.states[stateName] = st
      })

      return out
    },

    sanitizeAction(a) {
      if (!a || typeof a !== 'object') return {}

      const type = a.action

      if (type === 'send_text') {
        const obj = { action: 'send_text', text: a.text || '' }
        if (a.conditional) obj.conditional = JSON.parse(JSON.stringify(a.conditional))
        if (a.preview != null) obj.preview = !!a.preview
        return obj
      }

      if (type === 'send_buttons') {
        const sb = { action: 'send_buttons' }
        if (typeof a.header === 'string' && a.header.length) sb.header = a.header
        if (typeof a.body === 'string' && a.body.length) sb.body = a.body
        if (typeof a.footer === 'string' && a.footer.length) sb.footer = a.footer

        const btns = Array.isArray(a.buttons) ? a.buttons : []
        sb.buttons = btns.slice(0, 10).map(b => {
          const base = {
            id: b && b.id ? String(b.id) : '',
            title: b && b.title ? String(b.title) : '',
          }
          const ca = b && b.click_action
          if (ca === 'goto' || ca === 'set_mode' || ca === 'end') {
            base.click_action = ca
            if (ca === 'goto' && b && typeof b.goto_state === 'string') {
              base.goto_state = b.goto_state
            }
          }
          return base
        })

        if (a.quickReplyTemplate && a.quickReplyTemplate.name) {
          sb.quickReplyTemplate = {
            name: a.quickReplyTemplate.name,
            language: a.quickReplyTemplate.language || undefined,
            params: a.quickReplyTemplate.params || undefined,
          }
        }
        return sb
      }

      if (type === 'send_list') {
        const sl = { action: 'send_list', body: a.body || '', button_text: a.button_text || 'Ver opções' }
        if (typeof a.header === 'string' && a.header.length) sl.header = a.header
        if (typeof a.footer === 'string' && a.footer.length) sl.footer = a.footer

        const sections = Array.isArray(a.sections) ? a.sections : []
        sl.sections = sections.map(s => {
          const rows = Array.isArray(s.rows) ? s.rows : []
          const cleanRows = rows.map(r => {
            const base = {
              id: r && r.id ? String(r.id) : '',
              title: r && r.title ? String(r.title) : '',
            }
            if (r && typeof r.description === 'string' && r.description.length) base.description = r.description
            const ca = r && r.click_action
            if (ca === 'goto' || ca === 'set_mode' || ca === 'end') {
              base.click_action = ca
              if (ca === 'goto' && r && typeof r.goto_state === 'string') {
                base.goto_state = r.goto_state
              }
            }
            return base
          })
          const obj = { rows: cleanRows }
          if (typeof s.title === 'string' && s.title.length) obj.title = s.title
          return obj
        })
        return sl
      }

      if (type === 'ask_var') {
        return { action: 'ask_var', var: a.var || '', prompt: a.prompt || '' }
      }

      if (type === 'platform_lookup') {
        // Normalização de chaves para bater com o Schema:
        // UI pode usar "analise_de_pagamento"; Schema usa "analise_pagamento".
        const normalizeKey = k => (k === 'analise_de_pagamento' ? 'analise_pagamento' : k)

        const pl = { action: 'platform_lookup', op: a.op }
        if (a.messages) {
          if (a.op === 'link_transporte' || a.op === 'nota_fiscal') {
            pl.messages = {
              found: Array.isArray(a.messages.found) ? a.messages.found.map(m => this.sanitizeAction(m)).filter(x => x.action === 'send_text') : [],
              not_found: Array.isArray(a.messages.not_found) ? a.messages.not_found.map(m => this.sanitizeAction(m)).filter(x => x.action === 'send_text') : [],
            }
          } else if (a.op === 'status_pedido') {
            const bag = {};
            [
              'aguardando_pagamento',
              'aprovado',
              'cancelado',
              'analise_de_pagamento', // será mapeado
              'analise_pagamento', // já no formato do schema
              'em_transporte',
              'entregue',
            ].forEach(k => {
              const nk = normalizeKey(k)
              const src = a.messages[k] || a.messages[nk]
              if (Array.isArray(src)) {
                bag[nk] = src
                  .map(m => this.sanitizeAction(m))
                  .filter(x => x.action === 'send_text')
              }
            })
            // Garante que não sobra a chave errada
            if (bag.analise_de_pagamento && bag.analise_pagamento) {
              delete bag.analise_de_pagamento
            }
            pl.messages = bag
          }
        }
        return pl
      }

      if (type === 'validate_customer') {
        return { action: 'validate_customer' }
      }

      if (type === 'goto') return { action: 'goto', state: a.state || '' }

      if (type === 'set_mode') {
        const out = { action: 'set_mode', mode: a.mode || 'central' }

        if (a.assign_agent_id !== undefined && a.assign_agent_id !== null && String(a.assign_agent_id) !== '') {
          out.assign_agent_id = a.assign_agent_id
        }

        if (Array.isArray(a.assign_random_agent_ids) && a.assign_random_agent_ids.length) {
          const flat = a.assign_random_agent_ids
            .map(v => (v === 0 || v === '0' ? v : (v || '')))
            .filter(v => String(v).trim() !== '')
            .map(v => (typeof v === 'number' ? v : String(v)))
          const uniq = Array.from(new Set(flat))
          if (uniq.length > 0) out.assign_random_agent_ids = uniq
        }

        return out
      }

      if (type === 'set_tags') {
        const arr = Array.isArray(a.tags) ? a.tags : []
        const tags = Array.from(new Set(arr.map(v => String(v)).filter(s => s && s.trim() !== '')))
        return { action: 'set_tags', tags }
      }

      if (type === 'end') return { action: 'end' }

      return {}
    },

    /* ================= Versões ================= */
    async loadActive() {
      try {
        const resp = await FlowsAPI.getActiveVersion(this.botId)
        const data = resp && resp.data ? resp.data : {}
        const def = (typeof data.definition_json === 'string') ? JSON.parse(data.definition_json) : data.definition_json
        this.flow = def || this.defaultFlow()
        if (!this.flow.settings) this.$set(this.flow, 'settings', { autoclose_minutes: 0 })

        this.activeVersion = { id: data.id || data.version_id, version: data.version }
        this.activeVersionNumber = Number(data.version)
        this.selectedVersion = String(data.version)
        if (this.flow.start && this.flow.states[this.flow.start]) this.selectedState = this.flow.start
        else this.selectedState = null
      } catch (e) {
        this.activeVersion = null
        this.activeVersionNumber = null
      }
    },
    async reloadVersions() {
      if (!this.botId) return
      try {
        const resp = await FlowsAPI.listVersions(this.botId, { limit: 100, order: 'desc' })
        const d = resp && resp.data ? resp.data : {}
        const list = Array.isArray(d) ? d : (d.data || [])
        this.versions = list.map(it => ({ id: it.id, version: Number(it.version), created_at: it.created_at }))
        if (!this.selectedVersion && this.versions.length) {
          this.selectedVersion = String(this.versions[0].version)
        }
      } catch (e) {}
    },
    async onChangeVersion(val) {
      const vnum = parseInt(val, 10)
      if (!this.botId || !vnum) return
      try {
        const resp = await FlowsAPI.getVersion(this.botId, vnum)
        const data = resp && resp.data ? resp.data : {}
        const def = (typeof data.definition_json === 'string') ? JSON.parse(data.definition_json) : data.definition_json
        this.flow = def || this.defaultFlow()
        if (!this.flow.settings) this.$set(this.flow, 'settings', { autoclose_minutes: 0 })
        if (this.flow.start && this.flow.states[this.flow.start]) this.selectedState = this.flow.start
        else this.selectedState = null
        this.onFlowChange()
      } catch (e) {
        this.$bvToast && this.$bvToast.toast(`Não foi possível carregar a versão v${vnum}`, { variant: 'danger' })
      }
    },

    /* ================= Bot/versão ================= */
    async ensureBot() {
      if (this.botId) return this.botId
      const resp = await FlowsAPI.createBot({ store_id: this.storeId, nome: this.botName || 'Bot' })
      const data = resp && resp.data ? resp.data : {}
      this.$bvToast && this.$bvToast.toast(`Bot criado (#${data.bot_id})`, { variant: 'success' })
      this.$emit('update:botId', data.bot_id)
      this.botId = data.bot_id
      return this.botId
    },
    async saveDraft() {
      if (!this.isValid) { this.$bvToast && this.$bvToast.toast('Fluxo inválido', { variant: 'danger' }); return }
      const botId = await this.ensureBot()
      const toSave = this.validatableFlow
      const resp = await FlowsAPI.createVersion(botId, toSave)
      const data = resp && resp.data ? resp.data : {}
      this.lastDraftVersion = data.version
      this.selectedVersion = String(data.version)
      this.$bvToast && this.$bvToast.toast(`Rascunho salvo (v${data.version})`, { variant: 'success' })
      await this.reloadVersions()
    },
    async publishLastDraft() {
      if (!this.lastDraftVersion) return
      const resp = await FlowsAPI.publishVersion(this.botId, this.lastDraftVersion)
      const data = resp && resp.data ? resp.data : {}
      this.$bvToast && this.$bvToast.toast(`Publicado v${this.lastDraftVersion}`, { variant: 'warning' })
      this.activeVersion = { id: data.version_id, version: this.lastDraftVersion }
      this.activeVersionNumber = this.lastDraftVersion
      this.selectedVersion = String(this.lastDraftVersion)
      this.lastDraftVersion = null
      await this.reloadVersions()
    },
    async activateOnBot() {
      if (!this.botId) { this.$bvToast && this.$bvToast.toast('Crie/salve o bot primeiro.', { variant: 'warning' }); return }
      const ver = parseInt(this.selectedVersion, 10)
      if (!ver) { this.$bvToast && this.$bvToast.toast('Selecione uma versão para ativar.', { variant: 'warning' }); return }

      try {
        const resp = await FlowsAPI.publishVersion(this.botId, ver)
        const data = resp && resp.data ? resp.data : {}
        this.$bvToast && this.$bvToast.toast(`Versão v${ver} ativada no bot`, { variant: 'primary' })
        this.activeVersion = { id: data.version_id, version: ver }
        this.activeVersionNumber = ver
        await this.reloadVersions()
        await this.loadActive()
      } catch (e) {
        this.$bvToast && this.$bvToast.toast(`Falha ao ativar a versão no bot: ${e && e.message ? e.message : ''}`, { variant: 'danger' })
      }
    },

    /* ================= Estados ================= */
    addState() {
      const base = `estado_${this.stateNames.length + 1}`
      let name = base; let
        c = 1
      while (this.flow.states[name]) { name = `${base}_${c++}` }
      this.$set(this.flow.states, name, { on_enter: [], on_message: [] })
      if (!this.flow.start) this.flow.start = name
      this.selectedState = name
      this.onFlowChange()
    },
    createStateFromChild(name) {
      if (!name) return
      if (!this.flow.states[name]) {
        this.$set(this.flow.states, name, { on_enter: [], on_message: [] })
        this.$bvToast && this.$bvToast.toast(`Estado criado: ${name}`, { variant: 'info' })
      }
      this.onFlowChange()
    },
    ensureStateFromChild(name) {
      if (!name) return
      if (!this.flow.states[name]) {
        this.$set(this.flow.states, name, { on_enter: [], on_message: [] })
      }
      this.onFlowChange()
    },
    renameStateFromChild(payload) {
      if (!payload || !payload.oldName || !payload.newName) return
      const { oldName, newName } = payload
      if (oldName === newName) return
      if (!this.flow.states[oldName]) return
      if (this.flow.states[newName]) return
      const value = this.flow.states[oldName]
      this.$delete(this.flow.states, oldName)
      this.$set(this.flow.states, newName, value)
      if (this.flow.start === oldName) this.flow.start = newName
      if (this.selectedState === oldName) this.selectedState = newName
      Object.keys(this.flow.states).forEach(k => {
        const st = this.flow.states[k] || {};
        (st.on_enter || []).forEach(a => { if (a.action === 'goto' && a.state === oldName) a.state = newName });
        (st.on_message || []).forEach(r => {
          (r.then || []).forEach(a => { if (a && a.action === 'goto' && a.state === oldName) a.state = newName })
        })
      })
      this.onFlowChange()
    },
    renameState(oldName) {
      const newName = window.prompt('Novo nome do estado', oldName)
      if (!newName || newName === oldName) return
      if (this.flow.states[newName]) { this.$bvToast && this.$bvToast.toast('Já existe um estado com esse nome.', { variant: 'warning' }); return }
      const value = this.flow.states[oldName]
      this.$delete(this.flow.states, oldName)
      this.$set(this.flow.states, newName, value)
      if (this.flow.start === oldName) this.flow.start = newName
      if (this.selectedState === oldName) this.selectedState = newName
      Object.keys(this.flow.states).forEach(k => {
        const st = this.flow.states[k] || {};
        (st.on_enter || []).forEach(a => { if (a.action === 'goto' && a.state === oldName) a.state = newName });
        (st.on_message || []).forEach(r => {
          (r.then || []).forEach(a => { if (a && a.action === 'goto' && a.state === oldName) a.state = newName })
        })
      })
      this.onFlowChange()
    },
    removeState(name) {
      if (!window.confirm(`Remover estado "${name}"?`)) return
      this.$delete(this.flow.states, name)
      if (this.flow.start === name) this.flow.start = this.stateNames[0] || ''
      if (this.selectedState === name) this.selectedState = this.stateNames[0] || null
      Object.keys(this.flow.states).forEach(k => {
        const st = this.flow.states[k] || {}
        if (Array.isArray(st.on_enter)) {
          st.on_enter = st.on_enter.filter(a => !(a && a.action === 'goto' && a.state === name))
        }
        if (Array.isArray(st.on_message)) {
          st.on_message.forEach(r => {
            if (Array.isArray(r.then)) r.then = r.then.filter(a => !(a && a.action === 'goto' && a.state === name))
          })
          this.pruneRulesForState(k)
        }
      })
      this.onFlowChange()
    },

    /* ================= Coleta de IDs p/ badge ================= */
    autoInteractiveIdsFor(stateName) {
      const ids = []
      if (!stateName) return ids
      const st = this.flow.states[stateName]
      if (!st || !Array.isArray(st.on_enter)) return ids
      for (let i = 0; i < st.on_enter.length; i++) {
        const a = st.on_enter[i]
        if (!a) continue
        if (a.action === 'send_buttons' && Array.isArray(a.buttons)) {
          for (let j = 0; j < a.buttons.length; j++) {
            const b = a.buttons[j]
            if (b && b.id) ids.push(b.id)
          }
        } else if (a.action === 'send_list' && Array.isArray(a.sections)) {
          for (let s = 0; s < a.sections.length; s++) {
            const sec = a.sections[s]
            if (!sec || !Array.isArray(sec.rows)) continue
            for (let r = 0; r < sec.rows.length; r++) {
              const row = sec.rows[r]
              if (row && row.id) ids.push(row.id)
            }
          }
        }
      }
      return ids
    },

    /* ================= Poda de regras por interactive_reply_id ================= */
    pruneRulesForState(stateName) {
      if (!stateName) return
      const st = this.flow.states[stateName]
      if (!st || !Array.isArray(st.on_message)) return
      const keepIds = this.autoInteractiveIdsFor(stateName)
      st.on_message = st.on_message.filter(r => {
        const id = r && r.if && r.if.interactive_reply_id
        if (!id) return true
        return keepIds.indexOf(id) !== -1
      })
    },

    /* ================= Auto-regras vindas do ActionList ================= */
    onAutoRuleFor(payload) {
      if (!payload || !payload.id) return
      const stateName = payload.stateName || this.selectedState
      const st = this.flow.states[stateName]
      if (!st) return
      if (!Array.isArray(st.on_message)) this.$set(st, 'on_message', [])
      let idx = -1
      for (let i = 0; i < st.on_message.length; i++) {
        const rr = st.on_message[i]
        if (rr && rr.if && rr.if.interactive_reply_id === payload.id) { idx = i; break }
      }
      let thenActs = []
      if (payload.click_action === 'goto') {
        const to = payload.goto_state || payload.id
        thenActs = [{ action: 'goto', state: to }]
      } else if (payload.click_action === 'set_mode') {
        thenActs = [{ action: 'set_mode', mode: 'central' }]
      } else {
        thenActs = [{ action: 'end' }]
      }
      const newRule = { if: { interactive_reply_id: payload.id }, then: thenActs }
      if (idx === -1) st.on_message.push(newRule)
      else this.$set(st.on_message, idx, newRule)
      this.emitChangeAndPrune(stateName)
    },
    onAutoRuleRename(payload) {
      if (!payload || !payload.oldId || !payload.newId) return
      const stateName = payload.stateName || this.selectedState
      const st = this.flow.states[stateName]
      if (!st || !Array.isArray(st.on_message)) return
      for (let i = 0; i < st.on_message.length; i++) {
        const rr = st.on_message[i]
        if (rr && rr.if && rr.if.interactive_reply_id === payload.oldId) {
          this.$set(rr.if, 'interactive_reply_id', payload.newId)
        }
      }
      this.emitChangeAndPrune(stateName)
    },
    onAutoRuleRemove(payload) {
      if (!payload || !payload.id) return
      const stateName = payload.stateName || this.selectedState
      const st = this.flow.states[stateName]
      if (!st || !Array.isArray(st.on_message)) return
      st.on_message = st.on_message.filter(r => !(r && r.if && r.if.interactive_reply_id === payload.id))
      this.emitChangeAndPrune(stateName)
    },
    emitChangeAndPrune(stateName) {
      this.pruneRulesForState(stateName)
      this.onFlowChange()
    },

    /* ================= Util ================= */
    stateSummary(name) {
      const st = this.flow.states[name] || {}
      const a = (st.on_enter || []).length
      const r = (st.on_message || []).length
      return `${a} ação(ões), ${r} regra(s)`
    },
    addAction(stateName, list) {
      const st = this.flow.states[stateName]
      if (!st[list]) this.$set(st, list, [])
      st[list].push({ action: 'send_text', text: '' })
      this.onFlowChange()
    },
    addRule(stateName) {
      const st = this.flow.states[stateName]
      if (!st.on_message) this.$set(st, 'on_message', [])
      st.on_message.push({ if: {}, then: [] })
      this.onFlowChange()
    },
    onFlowChange() {
      if (this.selectedState) this.pruneRulesForState(this.selectedState)
      this.isValid // força revalidação
    },
    openJsonPreview() { this.$bvModal.show('modal-json') },

    /* ======= Integração diagrama -> flow ======= */
    onDiagramChange(newDef) {
      // newDef no formato { start, states, ... } vindo do FlowBuilder
      const next = JSON.parse(JSON.stringify(newDef || {}))
      next.settings = this.flow.settings || { autoclose_minutes: 0 }
      next.states = next.states || {}

      // preserva on_message dos estados existentes
      Object.keys(next.states).forEach(stateName => {
        const existed = this.flow.states && this.flow.states[stateName]
        const on_message = existed && Array.isArray(existed.on_message) ? existed.on_message : []
        if (!next.states[stateName].on_message) next.states[stateName].on_message = on_message
      })

      // garante start válido
      if (!next.start || !next.states[next.start]) {
        const first = Object.keys(next.states)[0]
        next.start = first || ''
      }

      this.flow = next
      if (!this.flow.states[this.selectedState]) this.selectedState = this.flow.start || null
      this.onFlowChange()
    },
  },
}
</script>

<style scoped>
.flow-states-list { max-height: 60vh; overflow: auto; }
.font-weight-600 { font-weight: 600; }

/* espaçamento leve do toggle no cabeçalho em telas médias+ */
@media (min-width: 768px){
  .ml-2 { margin-left: .5rem !important; }
}
</style>
